import { deepClone } from '@/utils/clone';
import type { ChipModelDto } from '@/schemas/chip';
import { createChipId, createChipInstanceId, createCircuitId } from '@/types/ids';

/** Independent runtime identities; tile/link identities remain local to the cloned chip. */
export function cloneChipDraft(source: ChipModelDto): ChipModelDto {
  const steps = cloneChipDraftSteps(source);
  let result = steps.next();
  while (!result.done) result = steps.next();
  return result.value;
}

/** Copy only data retained by drafts, yielding between large tile/edge batches. */
export function* cloneChipDraftSteps(source: ChipModelDto): Generator<void, ChipModelDto> {
  const emptyCircuit = (circuit: ChipModelDto['logicCircuitDto']) => ({
    ...circuit,
    tileManagerDto: { tiles: [] },
    connectionGraphDto: undefined,
  });
  const dto = deepClone({
    ...source,
    logicCircuitDto: emptyCircuit(source.logicCircuitDto),
    structureCircuitDto: emptyCircuit(source.structureCircuitDto),
    editingInstanceDto: { ...source.editingInstanceDto, logicGraphDto: { nodes: [], edges: [] } },
    chipInstanceDtos: source.chipInstanceDtos.map(instance => ({ ...instance, logicGraphDto: { nodes: [], edges: [] } })),
  }) as ChipModelDto;
  dto.id = createChipId();
  const instances = [dto.editingInstanceDto, ...dto.chipInstanceDtos];
  const ids = new Map(instances.map(instance => [instance.id, createChipInstanceId()]));
  for (const instance of instances) {
    instance.id = ids.get(instance.id)!;
    if (instance.originGlobalTileLocation) instance.originGlobalTileLocation.chipInstanceId = ids.get(instance.originGlobalTileLocation.chipInstanceId) ?? instance.originGlobalTileLocation.chipInstanceId;
    instance.logicGraphDto = { nodes: [], edges: [] };
    delete instance.templateToInstanceIdMap;
  }
  dto.editingInstanceDto.chipId = dto.id;
  for (const circuit of [dto.logicCircuitDto, dto.structureCircuitDto]) {
    circuit.id = createCircuitId();
    const sourceCircuit = circuit === dto.logicCircuitDto ? source.logicCircuitDto : source.structureCircuitDto;
    for (const original of sourceCircuit.tileManagerDto.tiles) {
      const tile = deepClone(original);
      tile.globalTileLocation.chipInstanceId = ids.get(tile.globalTileLocation.chipInstanceId) ?? tile.globalTileLocation.chipInstanceId;
      if (tile.meta.partOfChip) tile.meta.partOfChip = ids.get(tile.meta.partOfChip) ?? tile.meta.partOfChip;
      const state = tile.state as { connectedInterface?: { chipInstanceId: string } } | null;
      if (state?.connectedInterface) state.connectedInterface.chipInstanceId = ids.get(state.connectedInterface.chipInstanceId) ?? state.connectedInterface.chipInstanceId;
      delete tile.logicNodeIds;
      circuit.tileManagerDto.tiles.push(tile);
      if (circuit.tileManagerDto.tiles.length % 128 === 0) yield;
    }
    const connections = sourceCircuit.connectionGraphDto;
    if (connections) {
      const copied: typeof connections = { connections: [], ...(connections.disconnected ? { disconnected: [] } : {}) };
      circuit.connectionGraphDto = copied;
      for (const edge of connections.connections) {
        copied.connections.push(deepClone(edge));
        if (copied.connections.length % 256 === 0) yield;
      }
      for (const edge of connections.disconnected ?? []) {
        copied.disconnected!.push(deepClone(edge));
        if (copied.disconnected!.length % 256 === 0) yield;
      }
    }
  }
  return dto;
}
